import { GridValueGetterParams } from '@mui/x-data-grid-premium';
import { GridColumns } from 'shared/types';
import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ITableDataPageConfigProps, TableDataPage } from 'shared/components/TableDataPage';
import { formatUtcDateTimeToLocalDateTimeString, parseUtcDateTime } from 'shared/utility';
import { moneyFormatter, percentFormatter, toBackendFilterModel } from 'shared/utility/dataGrid';
import { DistributorsData } from 'types/backend';
import { defaultDistributorsConfig, distributorsContext } from './distributorManagementResolver';

export function DistributorManagement() {
    const distributorsContextValue = useContext(distributorsContext);
    const [isFirstRender, setIsFirstRender] = useState(true);

    useEffect(() => {
        if (isFirstRender && defaultDistributorsConfig) {
            const newTableDataRequest = {
                FilterModel: defaultDistributorsConfig.initialState?.filter?.filterModel
                    ? toBackendFilterModel(defaultDistributorsConfig.initialState.filter.filterModel)
                    : undefined,
                SortModel: defaultDistributorsConfig.initialState?.sorting?.sortModel,
                PageNumber: defaultDistributorsConfig.initialState?.pagination?.paginationModel?.page || 0,
                PageSize: defaultDistributorsConfig.initialState?.pagination?.paginationModel?.pageSize || 100,
            };
            if (
                JSON.stringify(distributorsContextValue.body.FilterModel) !==
                JSON.stringify(newTableDataRequest.FilterModel)
            ) {
                return distributorsContextValue.dontDebounceTableDataRequest(newTableDataRequest);
            }
            setIsFirstRender(false);
        }
    }, [distributorsContextValue, isFirstRender]);

    const tableConfig: ITableDataPageConfigProps = {
        pinnedColumnsProps: {
            pinAllColumns: true,
            pinnable: true,
            pinnedColumnsStorage: 'active-distibutor-management-pinned-columns',
        },
    };

    const columnConfig: GridColumns<DistributorsData> = [
        {
            headerName: 'id',
            field: 'id',
            hide: true,
            filterable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Customer Name',
            field: 'customerName',
            hideable: false,
            pinnable: false,
            renderCell: (params) => (
                <Link to={`/distributors/${params.row.customerNumber}/managePricing`}> {params.row.customerName} </Link>
            ),
            width: 300,
        },
        {
            headerName: 'Customer Number',
            field: 'customerNumber',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Customer Type',
            field: 'parentFlag',
            valueGetter: getCustomerType,
            hideable: false,
            pinnable: false,
            filterable: false,
            width: 200,
        },
        {
            headerName: 'Pricing Owner',
            field: 'pricingOwner',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Territory Sales Leader',
            field: 'territorySalesLeader',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Territory Sales Rep',
            field: 'territorySalesRep',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Region',
            field: 'region',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'City',
            field: 'city',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'State',
            field: 'state',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Price Tier',
            field: 'priceTier',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Price Trigger',
            field: 'priceTrigger',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Timing',
            field: 'timing',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'TTM Revenue',
            field: 'ttmRevenue',
            hideable: false,
            pinnable: false,
            type: 'number',
            valueFormatter: moneyFormatter,
            width: 200,
        },
        {
            headerName: 'TTM Pounds',
            field: 'ttmPounds',
            hideable: false,
            pinnable: false,
            type: 'number',
            width: 200,
        },
        {
            headerName: 'YOY Pounds Change',
            field: 'yoYPoundsChange',
            hideable: false,
            pinnable: false,
            type: 'number',
            valueFormatter: percentFormatter,
            width: 200,
        },
        {
            headerName: 'Distributor Branch Price Group',
            field: 'distributorBranchPriceGroup',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'POS Account',
            field: 'isPosAccount',
            hideable: false,
            pinnable: false,
            type: 'boolean',
            width: 200,
        },
        {
            headerName: 'Truckload Account',
            field: 'isTruckloadAccount',
            hideable: false,
            pinnable: false,
            type: 'boolean',
            width: 200,
        },
        {
            headerName: 'Alerts',
            field: 'alerts',
            hideable: false,
            pinnable: false,
            type: 'number',
            width: 100,
        },
        {
            headerName: 'Last Updated',
            field: 'deviationsLastUpdated',
            hideable: false,
            pinnable: false,
            valueGetter: (params) => parseUtcDateTime(params.value),
            valueFormatter: (params) => formatUtcDateTimeToLocalDateTimeString(params.value),
            type: 'dateTime',
            width: 200,
        },
    ];

    return (
        <TableDataPage
            title='Distributor Management'
            columnConfig={columnConfig}
            tableDataContext={distributorsContext}
            config={tableConfig}
        />
    );
}

export function getCustomerType(params: GridValueGetterParams) {
    return params.row.parentFlag ? 'Distributor' : 'Branch';
}
